/**
 * §13 — Cloudflare Turnstile: invisible bot check on signup, login, and the
 * first chat message (guide §5 services table). The widget script MUST load
 * from challenges.cloudflare.com (Cloudflare requirement — it is not on
 * cdnjs; don't "fix" the URL). Loaded without crossorigin (the endpoint
 * doesn't guarantee CORS headers, unlike cdnjs).
 *
 * No VITE_TURNSTILE_SITE_KEY at build time → every helper no-ops and returns
 * null/{} so dev, offline-demo, and pre-Turnstile backends never block.
 */

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '') as string;

interface TurnstileApi {
  render(host: HTMLElement, opts: Record<string, unknown>): string;
  remove(widgetId: string): void;
}

declare global { interface Window { turnstile?: TurnstileApi } }

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = TURNSTILE_SRC;
    el.async = true;
    const timer = window.setTimeout(() => reject(new Error('Turnstile CDN timeout')), 8000);
    el.onload = () => { window.clearTimeout(timer); resolve(); };
    el.onerror = () => {
      window.clearTimeout(timer);
      scriptPromise = null; // allow retry
      reject(new Error('Turnstile CDN failed'));
    };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

/**
 * Run an invisible challenge and resolve the token, or null on any failure —
 * callers send the request without the header and let the backend decide.
 * The host element only becomes visible if Cloudflare escalates to an
 * interactive check ('interaction-only' appearance).
 */
export async function getTurnstileToken(action: string, timeoutMs = 20000): Promise<string | null> {
  if (!SITE_KEY) return null;
  try { await loadTurnstileScript(); } catch { return null; }
  const ts = window.turnstile;
  if (!ts) return null;

  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;bottom:1rem;right:1rem;z-index:9999;';
  document.body.appendChild(host);

  return new Promise<string | null>((resolve) => {
    let widgetId: string | null = null;
    let settled = false;
    const finish = (token: string | null): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (widgetId !== null) { try { ts.remove(widgetId); } catch { /* already gone */ } }
      host.remove();
      resolve(token);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    try {
      widgetId = ts.render(host, {
        sitekey: SITE_KEY,
        action,
        appearance: 'interaction-only',
        theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
        callback: (token: string) => finish(token),
        'error-callback': () => finish(null),
        'expired-callback': () => finish(null),
      });
    } catch { finish(null); }
  });
}

/** Header fragment for protected endpoints; {} when Turnstile isn't configured. */
export async function turnstileHeader(action: string): Promise<Record<string, string>> {
  const token = await getTurnstileToken(action);
  return token ? { 'cf-turnstile-response': token } : {};
}
